import {
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
  HttpResponse,
} from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, of, finalize, shareReplay, tap } from 'rxjs';

type CacheEntry = {
  timestamp: number;
  response: HttpResponse<unknown>;
};

@Injectable()
export class DedupeGetInterceptor implements HttpInterceptor {
  private readonly inFlight = new Map<string, Observable<HttpEvent<unknown>>>();
  private readonly cache = new Map<string, CacheEntry>();
  private readonly ttlMs = 100;

  intercept(
    request: HttpRequest<unknown>,
    next: HttpHandler
  ): Observable<HttpEvent<unknown>> {
    if (request.method !== 'GET') {
      return next.handle(request);
    }

    const key = this.buildKey(request);
    const cached = this.cache.get(key);
    const now = Date.now();

    if (cached) {
      if (now - cached.timestamp < this.ttlMs) {
        return of(cached.response.clone());
      }
      this.cache.delete(key);
    }

    const existing = this.inFlight.get(key);
    if (existing) {
      return existing;
    }

    const shared$ = next.handle(request).pipe(
      tap((event) => {
        if (event instanceof HttpResponse) {
          this.cache.set(key, { timestamp: Date.now(), response: event.clone() });
        }
      }),
      finalize(() => this.inFlight.delete(key)),
      shareReplay({ bufferSize: 1, refCount: true })
    );

    this.inFlight.set(key, shared$);
    return shared$;
  }

  private buildKey(request: HttpRequest<unknown>): string {
    return [
      request.method,
      request.urlWithParams,
      request.responseType,
      request.withCredentials,
      this.serializeHeaders(request),
    ].join('|');
  }

  private serializeHeaders(request: HttpRequest<unknown>): string {
    return request.headers
      .keys()
      .sort()
      .map((key) => {
        const value = request.headers.getAll(key)?.join(',') ?? '';
        return `${key}:${value}`;
      })
      .join('|');
  }
}
